import Service, { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import moment from 'moment-timezone';

const WARNING_THRESHOLD = 75;
const BLOCK_THRESHOLD = 100;

export default class BasicVisitorsGatingService extends Service {
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;
  @service declare store: StoreService;

  @tracked monthlyCount: number | null = null;
  @tracked hasLoaded = false;
  @tracked isConnectTenant = false;

  get isFeatureEnabled(): boolean {
    return this.featureFlags.isEnabled('basic-100-visitors-gating');
  }

  get isBasicUser(): boolean {
    return !!this.state.vrSubscription?.isBasicUser;
  }

  get isActive(): boolean {
    return this.isFeatureEnabled && this.isBasicUser && !this.isConnectTenant;
  }

  get shouldShowWarning(): boolean {
    if (!this.isActive || this.monthlyCount == null) return false;
    return this.monthlyCount >= WARNING_THRESHOLD && this.monthlyCount < BLOCK_THRESHOLD;
  }

  get shouldBlock(): boolean {
    if (!this.isActive || this.monthlyCount == null) return false;
    return this.monthlyCount >= BLOCK_THRESHOLD;
  }

  get nextResetLabel(): string {
    const timezone = this.state.currentLocation?.timezone;
    const next = timezone
      ? moment.tz(timezone).add(1, 'month').startOf('month')
      : moment().add(1, 'month').startOf('month');
    return next.format('MMMM');
  }

  loadMonthlyCount = restartableTask(async () => {
    this.monthlyCount = null;
    this.isConnectTenant = false;

    if (!this.isFeatureEnabled || !this.isBasicUser) return;

    const location = this.state.currentLocation;
    if (!location) return;

    this.isConnectTenant = await location.isConnectedToProperty();
    if (this.isConnectTenant) return;

    const timezone = location.timezone || moment.tz.guess();
    const startOfMonth = moment.tz(timezone).startOf('month').toISOString();
    const endOfMonth = moment.tz(timezone).endOf('month').toISOString();

    try {
      const entries = await this.store.query('entry', {
        filter: {
          location: location.id,
          'start-date': startOfMonth,
          'end-date': endOfMonth,
        },
        page: { limit: 1 },
      });

      this.monthlyCount = (entries as unknown as { meta: { total: number } }).meta.total;
      this.hasLoaded = true;
    } catch {
      this.monthlyCount = null;
    }
  });
}

declare module '@ember/service' {
  interface Registry {
    'basic-visitors-gating': BasicVisitorsGatingService;
  }
}
